import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { ArrowRight, Phone, MessageSquare, Mail, Clock, MapPin, Calendar, CheckCircle, Globe, Linkedin, Instagram, Zap } from 'lucide-react';

const Contact = () => {
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    restaurantName: '',
    city: '',
    state: '',
    country: '',
    website: '',
    challenge: '',
    reviewCount: '',
    weeklyReservations: '',
    hearAbout: '',
    otherSource: ''
  });

  const handleScheduleDemo = () => {
    alert('Demo scheduling - will integrate with Calendly or similar booking system');
  };

  const handleSubmitForm = async (e) => {
    e.preventDefault();
    
    try {
      const response = await fetch('/api/contact/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        alert('Thank you for your submission! We\'ll get back to you within 4 hours during business hours.');
        // Reset form
        setFormData({
          fullName: '',
          email: '',
          phone: '',
          restaurantName: '',
          city: '',
          state: '',
          country: '',
          website: '',
          challenge: '',
          reviewCount: '',
          weeklyReservations: '',
          hearAbout: '',
          otherSource: ''
        });
      } else {
        const errorData = await response.json();
        alert(`Error submitting form: ${errorData.detail || 'Please try again.'}`);
      }
    } catch (error) {
      alert('Network error. Please check your connection and try again.');
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleWhatsApp = (number) => {
    alert(`Opening WhatsApp to ${number} - will integrate with WhatsApp deep linking`);
  };

  const demoFeatures = [
    "15-minute video call (Zoom or Google Meet)",
    "Live demo tailored to your restaurant type",
    "Honest assessment of which plan fits your needs",
    "Q&A—ask us anything",
    "Custom growth projection for your specific situation",
    "Zero pressure to buy"
  ];

  const contactMethods = [
    {
      icon: Phone,
      title: "Schedule a Free Demo",
      description: "See Isabella AI in action. Walk through the dashboard. Get a custom growth strategy for your restaurant.",
      cta: "Schedule Demo Call",
      action: handleScheduleDemo,
      color: "from-[#0A7A6E] to-[#085A54]"
    },
    {
      icon: MessageSquare,
      title: "Send Us a Message",
      description: "Fill out our detailed form and we'll respond within 4 hours during business hours.",
      cta: "Fill Contact Form",
      action: () => document.getElementById('contact-form').scrollIntoView({ behavior: 'smooth' }),
      color: "from-[#D4623C] to-[#B8532F]"
    }
  ];

  const whatsappNumbers = [
    {
      flag: "🇨🇦 🇺🇸",
      region: "Canada/USA",
      number: "+1 (647) 555-0123"
    },
    {
      flag: "🇮🇳",
      region: "India", 
      number: "+91 98765 43210"
    }
  ];

  const emailContacts = [
    { type: "General Inquiries", email: "hello@prismospark.com", icon: Mail },
    { type: "Support (Existing Clients)", email: "support@prismospark.com", icon: CheckCircle },
    { type: "Partnership Opportunities", email: "partners@prismospark.com", icon: Globe },
    { type: "Media/Press", email: "press@prismospark.com", icon: MessageSquare }
  ];

  const businessHours = [
    {
      region: "North America (Toronto/Seattle)",
      schedule: [
        "Monday-Friday: 9:00 AM - 6:00 PM EST/PST",
        "Saturday: 10:00 AM - 2:00 PM EST/PST", 
        "Sunday: Closed"
      ]
    },
    {
      region: "India (Mumbai)",
      schedule: [
        "Monday-Saturday: 10:00 AM - 7:00 PM IST",
        "Sunday: Closed"
      ]
    }
  ];

  return (
    <div className="pt-16 overflow-hidden">
      {/* Hero Section */}
      <section className="relative min-h-[80vh] bg-gradient-to-br from-brand-charcoal via-brand-charcoal-700 to-brand-charcoal flex items-center">
        {/* Background Elements */}
        <div className="absolute inset-0">
          <div className="absolute top-20 right-20 w-64 h-64 bg-gradient-to-br from-[#2D6A4F]/20 to-[#2D6A4F]/20 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-20 left-20 w-64 h-64 bg-gradient-to-br from-brand-sienna/20 to-brand-gold/20 rounded-full blur-3xl animate-pulse delay-1000"></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="text-center">
            <Badge className="inline-block mb-8 bg-white/10 backdrop-blur-md border border-white/20 text-white px-6 py-3">
              Contact Us
            </Badge>
            <h1 className="text-5xl lg:text-7xl font-bold text-white mb-8 leading-tight">
              Let's Talk About Growing
              <span className="block bg-gradient-to-r from-[#2D6A4F] to-[#2D6A4F] bg-clip-text text-transparent animate-gradient bg-300%">
                Your Restaurant
              </span>
            </h1>
            <p className="text-2xl lg:text-3xl text-brand-ivory/90 max-w-4xl mx-auto mb-16 leading-relaxed">
              Book a free 15-minute consultation. We'll show you exactly how PrismoSpark would work for your restaurant—no pressure, no sales pitch.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Methods */}
      <section className="py-32 bg-gradient-to-br from-brand-ivory via-brand-ivory-50 to-brand-ivory">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12">
            {contactMethods.map((method, index) => (
              <Card key={index} className="group overflow-hidden shadow-xl border-2 border-brand-charcoal/20 hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-4">
                <CardHeader className="p-12">
                  <div className="flex items-center space-x-4 mb-6">
                    <div className={`w-16 h-16 bg-gradient-to-br ${method.color} rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300 shadow-lg`}>
                      <method.icon className="text-white" size={28} />
                    </div>
                  </div>
                  <CardTitle className="text-3xl text-brand-charcoal mb-4 group-hover:text-[#2D6A4F] transition-colors duration-300">
                    {method.title}
                  </CardTitle>
                  <CardDescription className="text-brand-charcoal-600 text-lg leading-relaxed mb-8">
                    {method.description}
                  </CardDescription>

                  {index === 0 && (
                    <div className="space-y-4 mb-8">
                      <h4 className="text-xl font-bold text-brand-charcoal">What to expect:</h4>
                      <div className="space-y-3">
                        {demoFeatures.map((feature, idx) => (
                          <div key={idx} className="flex items-start space-x-3">
                            <CheckCircle className="w-5 h-5 text-[#2D6A4F] mt-1 flex-shrink-0" />
                            <span className="text-brand-charcoal-600">{feature}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <Button 
                    onClick={method.action}
                    className={`w-full bg-gradient-to-r ${method.color} hover:shadow-lg text-white font-semibold text-lg px-8 py-4 transition-all duration-300 transform hover:scale-105`}
                  >
                    {method.cta}
                    <ArrowRight className="ml-2" size={20} />
                  </Button>
                </CardHeader>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section id="contact-form" className="py-32 bg-brand-ivory-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl lg:text-5xl font-bold text-brand-charcoal mb-6">Send Us a Message</h2>
            <p className="text-xl text-brand-charcoal-600">
              Response time: We respond within 4 hours during business hours (next business day for after-hours inquiries).
            </p>
          </div>

          <Card className="shadow-2xl border-2 border-brand-charcoal/20">
            <CardContent className="p-12">
              <form onSubmit={handleSubmitForm} className="space-y-8">
                {/* Your Information */}
                <div>
                  <h3 className="text-2xl font-bold text-brand-charcoal mb-6">Your Information:</h3>
                  <div className="grid md:grid-cols-3 gap-6">
                    <div>
                      <label className="block text-sm font-semibold text-brand-charcoal mb-2">Full Name*</label>
                      <input
                        type="text"
                        name="fullName"
                        required
                        value={formData.fullName}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-brand-charcoal/30 rounded-lg focus:ring-2 focus:ring-[#2D6A4F] focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-brand-charcoal mb-2">Email*</label>
                      <input
                        type="email"
                        name="email"
                        required
                        value={formData.email}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-brand-charcoal/30 rounded-lg focus:ring-2 focus:ring-[#2D6A4F] focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-brand-charcoal mb-2">Phone Number*</label>
                      <input
                        type="tel"
                        name="phone"
                        required
                        value={formData.phone}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-brand-charcoal/30 rounded-lg focus:ring-2 focus:ring-[#2D6A4F] focus:border-transparent"
                      />
                    </div>
                  </div>
                </div>

                {/* Restaurant Details */}
                <div>
                  <h3 className="text-2xl font-bold text-brand-charcoal mb-6">Restaurant Details:</h3>
                  <div className="grid md:grid-cols-2 gap-6 mb-6">
                    <div>
                      <label className="block text-sm font-semibold text-brand-charcoal mb-2">Restaurant Name*</label>
                      <input
                        type="text"
                        name="restaurantName"
                        required
                        value={formData.restaurantName}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-brand-charcoal/30 rounded-lg focus:ring-2 focus:ring-[#2D6A4F] focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-brand-charcoal mb-2">City*</label>
                      <input
                        type="text"
                        name="city"
                        required
                        value={formData.city}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-brand-charcoal/30 rounded-lg focus:ring-2 focus:ring-[#2D6A4F] focus:border-transparent"
                      />
                    </div>
                  </div>
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-semibold text-brand-charcoal mb-2">State/Province*</label>
                      <input
                        type="text"
                        name="state"
                        required
                        value={formData.state}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-brand-charcoal/30 rounded-lg focus:ring-2 focus:ring-[#2D6A4F] focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-brand-charcoal mb-2">Country*</label>
                      <input
                        type="text"
                        name="country"
                        required
                        value={formData.country}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-brand-charcoal/30 rounded-lg focus:ring-2 focus:ring-[#2D6A4F] focus:border-transparent"
                      />
                    </div>
                  </div>
                </div>

                {/* Tell Us About Your Challenge */}
                <div>
                  <h3 className="text-2xl font-bold text-brand-charcoal mb-6">Tell Us About Your Challenge:</h3>
                  <div className="space-y-6">
                    <div>
                      <label className="block text-sm font-semibold text-brand-charcoal mb-2">Current website (URL if you have one)</label>
                      <input
                        type="url"
                        name="website"
                        value={formData.website}
                        onChange={handleInputChange}
                        placeholder="https://your-restaurant.com"
                        className="w-full px-4 py-3 border border-brand-charcoal/30 rounded-lg focus:ring-2 focus:ring-[#2D6A4F] focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-brand-charcoal mb-2">Biggest online marketing challenge right now</label>
                      <textarea
                        name="challenge"
                        value={formData.challenge}
                        onChange={handleInputChange}
                        rows="4"
                        className="w-full px-4 py-3 border border-brand-charcoal/30 rounded-lg focus:ring-2 focus:ring-brand-teal focus:border-transparent"
                        placeholder="Describe your main challenge with online marketing, website, reviews, bookings, etc."
                      ></textarea>
                    </div>
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-semibold text-brand-charcoal mb-2">Current monthly review count (estimate)</label>
                        <input
                          type="text"
                          name="reviewCount"
                          value={formData.reviewCount}
                          onChange={handleInputChange}
                          placeholder="e.g., 2-3 reviews per month"
                          className="w-full px-4 py-3 border border-brand-charcoal/30 rounded-lg focus:ring-2 focus:ring-brand-teal focus:border-transparent"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-brand-charcoal mb-2">How many reservations do you get weekly?</label>
                        <input
                          type="text"
                          name="weeklyReservations"
                          value={formData.weeklyReservations}
                          onChange={handleInputChange}
                          placeholder="e.g., 15-20 per week"
                          className="w-full px-4 py-3 border border-brand-charcoal/30 rounded-lg focus:ring-2 focus:ring-brand-teal focus:border-transparent"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* How did you hear about us */}
                <div>
                  <h3 className="text-2xl font-bold text-brand-charcoal mb-6">How did you hear about us?</h3>
                  <div className="space-y-3">
                    {[
                      'Google Search',
                      'Restaurant owner referral', 
                      'Social media',
                      'Industry publication',
                      'Other'
                    ].map((option) => (
                      <label key={option} className="flex items-center space-x-3">
                        <input
                          type="radio"
                          name="hearAbout"
                          value={option}
                          onChange={handleInputChange}
                          className="w-4 h-4 text-[#2D6A4F] focus:ring-[#2D6A4F]"
                        />
                        <span className="text-brand-charcoal-600">{option}</span>
                      </label>
                    ))}
                  </div>
                  {formData.hearAbout === 'Other' && (
                    <div className="mt-4">
                      <input
                        type="text"
                        name="otherSource"
                        value={formData.otherSource}
                        onChange={handleInputChange}
                        placeholder="Please specify..."
                        className="w-full px-4 py-3 border border-brand-charcoal/30 rounded-lg focus:ring-2 focus:ring-brand-teal focus:border-transparent"
                      />
                    </div>
                  )}
                </div>

                <Button 
                  type="submit"
                  className="w-full bg-[#2D6A4F] hover:bg-[#2D6A4F] text-white font-bold text-lg px-8 py-4 transition-all duration-300 transform hover:scale-105"
                >
                  <Mail className="mr-2" size={20} />
                  Submit Message
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* WhatsApp & Other Contact Methods */}
      <section className="py-32 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* WhatsApp */}
          <div className="mb-20">
            <div className="text-center mb-12">
              <div className="text-6xl mb-4">📱</div>
              <h2 className="text-4xl lg:text-5xl font-bold text-brand-charcoal mb-6">WhatsApp (Fastest Response)</h2>
              <p className="text-xl text-brand-charcoal-600 max-w-3xl mx-auto">
                Prefer texting? Message us directly on WhatsApp. Available for quick questions, screenshots, or scheduling calls.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
              {whatsappNumbers.map((contact, index) => (
                <Card key={index} className="group hover:shadow-lg transition-all duration-300 transform hover:-translate-y-2 border-2 border-[#2D6A4F]/30 hover:border-[#2D6A4F]">
                  <CardContent className="p-8 text-center">
                    <div className="text-4xl mb-4">{contact.flag}</div>
                    <h3 className="text-xl font-bold text-brand-charcoal mb-2">{contact.region}</h3>
                    <p className="text-2xl font-bold text-[#2D6A4F] mb-6">{contact.number}</p>
                    <Button 
                      onClick={() => handleWhatsApp(contact.number)}
                      className="bg-[#2D6A4F] hover:bg-[#2D6A4F] text-white font-semibold px-6 py-3 transition-all duration-300"
                    >
                      <MessageSquare className="mr-2" size={20} />
                      Message on WhatsApp
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>

          {/* Email */}
          <div className="mb-20">
            <div className="text-center mb-12">
              <div className="text-6xl mb-4">📧</div>
              <h2 className="text-4xl lg:text-5xl font-bold text-brand-charcoal mb-6">Email</h2>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              {emailContacts.map((contact, index) => (
                <Card key={index} className="group hover:shadow-lg transition-all duration-300 transform hover:-translate-y-2">
                  <CardContent className="p-8">
                    <div className="flex items-center space-x-4 mb-4">
                      <div className="w-12 h-12 bg-gradient-to-br from-[#2D6A4F] to-[#2D6A4F] rounded-lg flex items-center justify-center">
                        <contact.icon className="text-white" size={20} />
                      </div>
                      <div>
                        <h3 className="text-lg font-bold text-brand-charcoal">{contact.type}</h3>
                        <a href={`mailto:${contact.email}`} className="text-[#2D6A4F] hover:underline font-semibold">
                          {contact.email}
                        </a>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>

          {/* Business Hours */}
          <div className="mb-20">
            <div className="text-center mb-12">
              <div className="text-6xl mb-4">🕐</div>
              <h2 className="text-4xl lg:text-5xl font-bold text-brand-charcoal mb-6">Business Hours</h2>
            </div>

            <div className="grid lg:grid-cols-2 gap-8 max-w-5xl mx-auto">
              {businessHours.map((hours, index) => (
                <Card key={index} className="border-2 border-brand-charcoal/20">
                  <CardHeader className="bg-gradient-to-r from-brand-charcoal to-brand-charcoal-700 text-white p-8">
                    <CardTitle className="text-2xl">{hours.region}</CardTitle>
                  </CardHeader>
                  <CardContent className="p-8">
                    <div className="space-y-3">
                      {hours.schedule.map((time, idx) => (
                        <div key={idx} className="flex items-center space-x-2">
                          <Clock className="w-4 h-4 text-brand-charcoal-600" />
                          <span className="text-brand-charcoal-600">{time}</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            <div className="text-center mt-8">
              <Card className="max-w-2xl mx-auto bg-gradient-to-r from-brand-amber/10 to-brand-amber/20 border-2 border-brand-amber/30">
                <CardContent className="p-6">
                  <p className="text-brand-amber font-semibold">
                    <Zap className="inline w-5 h-5 mr-2" />
                    Emergency support for existing clients: 24/7 via email/chat
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Office Location */}
          <div>
            <div className="text-center mb-12">
              <div className="text-6xl mb-4">🌍</div>
              <h2 className="text-4xl lg:text-5xl font-bold text-brand-charcoal mb-6">Office Location</h2>
            </div>

            <Card className="max-w-2xl mx-auto shadow-xl border-2 border-brand-charcoal/20">
              <CardHeader className="bg-gradient-to-r from-[#2D6A4F] to-[#2D6A4F] text-white p-8 text-center">
                <CardTitle className="text-2xl">Amritsar, India (Headquarters)</CardTitle>
              </CardHeader>
              <CardContent className="p-8 text-center">
                <div className="space-y-2 text-brand-charcoal-600 mb-6">
                  <p className="font-semibold">Block D, Ranjit Avenue</p>
                  <p>Amritsar, Punjab 143001</p>
                  <p>India</p>
                </div>
                <div className="bg-brand-gold/10 border-2 border-brand-gold/30 rounded-lg p-4">
                  <p className="text-brand-gold font-semibold">
                    Office open by appointment only. Virtual meetings preferred.
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Follow Us */}
      <section className="py-20 bg-brand-ivory-50">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <div className="text-6xl mb-6">🔗</div>
          <h2 className="text-3xl lg:text-4xl font-bold text-brand-charcoal mb-8">Follow Us</h2>
          
          <div className="flex justify-center space-x-8">
            <a 
              href="https://linkedin.com/company/prismospark" 
              target="_blank" 
              rel="noopener noreferrer"
              className="group flex items-center space-x-3 bg-brand-teal hover:bg-brand-teal-600 text-white px-8 py-4 rounded-xl transition-all duration-300 transform hover:scale-105"
            >
              <Linkedin size={24} />
              <span className="font-semibold">/company/prismospark</span>
            </a>
            <a 
              href="https://instagram.com/prismospark" 
              target="_blank" 
              rel="noopener noreferrer"
              className="group flex items-center space-x-3 bg-brand-sienna hover:bg-brand-sienna-600 text-white px-8 py-4 rounded-xl transition-all duration-300 transform hover:scale-105"
            >
              <Instagram size={24} />
              <span className="font-semibold">@prismospark</span>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;